import { useEffect, useId, useState } from "react";
import { Link } from "react-router";
import { INTERESTS, validateContact } from "../../lib/contactValidation.js";
import { SITE } from "../../lib/site.js";
import { track } from "../../lib/analytics.js";
import styles from "./ContactForm.module.css";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  company: "",
  title: "",
  interest: "general",
  message: "",
};

// The button names what it does, and the thank-you repeats it.
const ACTION = {
  demo: { button: "Request a demo", sent: "Demo requested" },
  webinar: { button: "Request a webinar", sent: "Webinar requested" },
  general: { button: "Send message", sent: "Message sent" },
};

export default function ContactForm({ interest = "general", page = "/contact" }) {
  const [values, setValues] = useState({ ...EMPTY, interest });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | failed
  // The endpoint comes from the build; the window override is a Playwright-only hook, read after
  // mount so the prerendered HTML and the first client render match.
  const [target, setTarget] = useState(SITE.contactEndpoint);
  useEffect(() => {
    if (!SITE.contactEndpoint && typeof window !== "undefined" && window.__RDV_CONTACT_ENDPOINT) {
      // Playwright-only override, read post-mount so the prerendered HTML and the first client
      // render both use SITE.contactEndpoint; only this deferred update picks up the window global.
      setTarget(window.__RDV_CONTACT_ENDPOINT);
    }
  }, []);
  // useState's lazy initializer form runs the impure Date.now() call once, not on every render
  // (a bare useRef(Date.now()) or ref.current = Date.now() in the render body both re-evaluate it).
  const [openedAt] = useState(() => Date.now());
  const id = useId();
  const action = ACTION[values.interest] || ACTION.general;

  const set = (name, value) => setValues((v) => ({ ...v, [name]: value }));
  const field = (name) => ({
    id: `${id}-${name}`,
    name,
    value: values[name],
    onChange: (e) => set(name, e.target.value),
    "aria-invalid": errors[name] ? "true" : undefined,
    "aria-describedby": errors[name] ? `${id}-${name}-error` : undefined,
  });
  const error = (name) =>
    errors[name] ? (
      <p id={`${id}-${name}-error`} className={styles.error}>
        {errors[name]}
      </p>
    ) : null;
  const label = (name, text, optional = false) => (
    <label htmlFor={`${id}-${name}`}>
      {text}
      {optional ? <span className={styles.optional}> (optional)</span> : null}
    </label>
  );

  async function onSubmit(event) {
    event.preventDefault();
    const check = validateContact(values);
    setErrors(check.errors);
    if (!check.valid) {
      const first = Object.keys(check.errors)[0];
      const el =
        first === "interest"
          ? document.querySelector(`input[name="${id}-interest"]`)
          : document.getElementById(`${id}-${first}`);
      el?.focus();
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(target, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ...values,
          page,
          _hp: event.target.elements.website.value,
          _t: openedAt,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok && body.ok) {
        setStatus("sent");
        // Never the name, email or message: GA4 must not receive personal data.
        track(values.interest === "demo" ? "demo_request_submit" : "contact_submit", {
          interest: values.interest,
          form_page: page,
        });
      } else if (res.status === 400 && body.errors) {
        setErrors(body.errors);
        setStatus("idle");
      } else {
        setStatus("failed");
      }
    } catch {
      setStatus("failed");
    }
  }

  if (!target) {
    return (
      <div className={styles.note} role="note">
        <h2 className={styles.noteTitle}>Email or call us</h2>
        <p>
          Write to <a href={`mailto:${SITE.email}`}>{SITE.email}</a> or call{" "}
          <a href={SITE.phoneHref}>{SITE.phone}</a>. We answer within one business day.
        </p>
      </div>
    );
  }
  if (status === "sent") {
    return (
      <div className={styles.note} role="status">
        <h2 className={styles.noteTitle}>{action.sent}. Thank you.</h2>
        <p>
          We will reply to {values.email} within one business day.
          <br />
          In a hurry? Call <a href={SITE.phoneHref}>{SITE.phone}</a>.
        </p>
      </div>
    );
  }
  return (
    <form
      className={styles.form}
      onSubmit={onSubmit}
      noValidate
      aria-describedby={status === "failed" ? `${id}-failed` : undefined}
    >
      <fieldset
        className={styles.choices}
        aria-describedby={errors.interest ? `${id}-interest-error` : undefined}
      >
        <legend>How can we help?</legend>
        <div className={styles.options}>
          {INTERESTS.map((i) => (
            <label key={i.value} className={styles.option}>
              <input
                type="radio"
                name={`${id}-interest`}
                value={i.value}
                checked={values.interest === i.value}
                onChange={() => set("interest", i.value)}
              />
              <span>{i.label}</span>
            </label>
          ))}
        </div>
        {error("interest")}
      </fieldset>

      <div className={styles.row}>
        <div className={styles.field}>
          {label("name", "Name")}
          <input {...field("name")} type="text" autoComplete="name" required />
          {error("name")}
        </div>
        <div className={styles.field}>
          {label("email", "Work email")}
          <input
            {...field("email")}
            type="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@agency.gov"
            required
          />
          {error("email")}
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.field}>
          {label("company", "Organization", true)}
          <input {...field("company")} type="text" autoComplete="organization" />
        </div>
        <div className={styles.field}>
          {label("title", "Job title", true)}
          <input {...field("title")} type="text" autoComplete="organization-title" />
        </div>
      </div>
      <div className={styles.field}>
        {label("phone", "Phone", true)}
        <input {...field("phone")} type="tel" autoComplete="tel" inputMode="tel" />
        {error("phone")}
      </div>
      <div className={styles.field}>
        {label("message", "Tell us about your project")}
        <textarea
          {...field("message")}
          rows="5"
          placeholder="For example: an intersection sight distance audit of 12 intersections on SR-40, due in March."
          required
        />
        {error("message")}
      </div>

      <div className={styles.honeypot} aria-hidden="true">
        <label htmlFor={`${id}-website`}>Website</label>
        <input
          id={`${id}-website`}
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          defaultValue=""
        />
      </div>

      {status === "failed" ? (
        <p id={`${id}-failed`} className={styles.alert} role="alert">
          We could not send your message. Please email{" "}
          <a href={`mailto:${SITE.email}`}>{SITE.email}</a> instead.
        </p>
      ) : null}

      <div className={styles.submit}>
        <button
          type="submit"
          className={`btn btn-primary ${styles.button}`}
          disabled={status === "sending"}
        >
          {status === "sending" ? "Sending…" : action.button}
        </button>
        <p className={styles.promise}>
          We reply within one business day. We use your details only to answer you; see our{" "}
          <Link to="/privacy-policy">privacy policy</Link>.
        </p>
      </div>
    </form>
  );
}
